import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,} from "@mui/material";
import {AavoNumberField} from "src/components/common/inputFields/AavoNumberField.tsx";
import {
    CustomerOrderProfitabilityLineKey,
} from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityLineKey.ts";
import {
    CustomerOrderProfitabilityRowDto
} from "src/api/generated/erp/sales/customerOrder/profitability/model/customerOrderProfitabilityRowDto.ts";
import {
    getCustomerOrderProfitabilityDisplayLabel,
    isCustomerOrderProfitabilityLineKeyEditable
} from "src/components/views/erp/sales/customerOrder/profitability/customerOrderProfitabilityLineKeyUtils.ts";
import {
    formatCustomerOrderProfitabilityComparisonAmount,
    formatCustomerOrderProfitabilityValue,
    fromCustomerOrderProfitabilityDisplayAmount,
    isGoodCustomerOrderProfitabilityComparisonDisplayAmount,
    toCustomerOrderProfitabilityDisplayAmount,
} from "src/components/views/erp/sales/customerOrder/profitability/customerOrderProfitabilityDisplayUtils.ts";
import { formatLabelWithCurrencyCode } from "src/components/views/erp/sales/salesUtils.ts";
import i18n from "i18next";
import {useEffect, useState} from "react";

interface CustomerOrderProfitabilityTableProps {
    rows: Array<CustomerOrderProfitabilityRowDto>;
    domesticCurrencyCode: string;
    isEditMode: boolean;
    onBudgetChange: (row: CustomerOrderProfitabilityRowDto, value: number) => void;
    onForecastChange: (row: CustomerOrderProfitabilityRowDto, value: number) => void;
}

export const CustomerOrderProfitabilityTable = ({
                                                    rows,
                                                    domesticCurrencyCode,
                                                    isEditMode,
                                                    onBudgetChange,
                                                    onForecastChange,
                                                }: CustomerOrderProfitabilityTableProps) => {
    return (
        <TableContainer
            sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
            }}
        >
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{i18n.t("row")}</TableCell>
                        <TableCell align="right">{formatLabelWithCurrencyCode(i18n.t("budget"), domesticCurrencyCode)}</TableCell>
                        <TableCell align="right">{formatLabelWithCurrencyCode(i18n.t("forecast"), domesticCurrencyCode)}</TableCell>
                        <TableCell align="right">{formatLabelWithCurrencyCode(i18n.t("actual"), domesticCurrencyCode)}</TableCell>
                        <TableCell align="right">{formatLabelWithCurrencyCode(i18n.t("actual_vs_budget"), domesticCurrencyCode)}</TableCell>
                        <TableCell align="right">{formatLabelWithCurrencyCode(i18n.t("actual_vs_forecast"), domesticCurrencyCode)}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.lineKey}>
                            <TableCell>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        paddingLeft: `${getCustomerOrderProfitabilityLineKeyLevel(row.lineKey) * 1.5}rem`,
                                        fontWeight: getCustomerOrderProfitabilityLineKeyLevel(row.lineKey) === 0 ? 600 : 400,
                                    }}
                                >
                                    {getCustomerOrderProfitabilityDisplayLabel(row.lineKey)}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <AmountCell
                                    lineKey={row.lineKey}
                                    value={row.budget}
                                    domesticCurrencyCode={domesticCurrencyCode}
                                    editable={isEditMode && isCustomerOrderProfitabilityLineKeyEditable(row.lineKey)}
                                    onChange={(value) => onBudgetChange(row, value)}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <AmountCell
                                    lineKey={row.lineKey}
                                    value={row.forecast}
                                    domesticCurrencyCode={domesticCurrencyCode}
                                    editable={isEditMode && isCustomerOrderProfitabilityLineKeyEditable(row.lineKey)}
                                    onChange={(value) => onForecastChange(row, value)}
                                />
                            </TableCell>
                            <TableCell align="right">
                                {formatCustomerOrderProfitabilityValue(
                                    row.lineKey,
                                    toCustomerOrderProfitabilityDisplayAmount(row.lineKey, row.actual),
                                    domesticCurrencyCode,
                                )}
                            </TableCell>
                            <TableCell align="right">
                                <ComparisonAmountCell
                                    lineKey={row.lineKey}
                                    value={row.actual - row.budget}
                                    domesticCurrencyCode={domesticCurrencyCode}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <ComparisonAmountCell
                                    lineKey={row.lineKey}
                                    value={row.actual - row.forecast}
                                    domesticCurrencyCode={domesticCurrencyCode}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

interface AmountCellProps {
    lineKey: CustomerOrderProfitabilityLineKey;
    value: number;
    domesticCurrencyCode: string;
    editable: boolean;
    onChange: (value: number) => void;
}

const AmountCell = ({lineKey, value: valueInitial, domesticCurrencyCode, editable, onChange}: AmountCellProps) => {
    const [value, setValue] = useState(toCustomerOrderProfitabilityDisplayAmount(lineKey, valueInitial));

    useEffect(() => {
        setValue(toCustomerOrderProfitabilityDisplayAmount(lineKey, valueInitial));
    }, [lineKey, valueInitial]);
    if (!editable) {
        return formatCustomerOrderProfitabilityValue(
            lineKey,
            toCustomerOrderProfitabilityDisplayAmount(lineKey, valueInitial),
            domesticCurrencyCode,
        );
    }

    return (
        <AavoNumberField
            size="small"
            variant="standard"
            hiddenLabel
            margin="none"
            value={value}
            onChange={(nextValue) => setValue(nextValue ?? 0)}
            onSubmit={(nextValue) => {
                const nextRawValue = fromCustomerOrderProfitabilityDisplayAmount(lineKey, nextValue ?? 0);
                const nextDisplayValue = toCustomerOrderProfitabilityDisplayAmount(lineKey, nextRawValue);

                setValue(nextDisplayValue)
                onChange(nextRawValue);
            }}
            type="decimal"
            sx={{
                width: 120,
                "& .MuiInputBase-root": {
                    fontSize: "inherit",
                },
                "& .MuiInputBase-input": {
                    textAlign: "right",
                    paddingTop: "1px",
                    paddingBottom: "1px",
                },
            }}
        />
    );
};

interface ComparisonAmountCellProps {
    lineKey: CustomerOrderProfitabilityLineKey;
    value: number;
    domesticCurrencyCode: string;
}

const ComparisonAmountCell = ({lineKey, value, domesticCurrencyCode}: ComparisonAmountCellProps) => {
    if (lineKey === "MARGIN_PERCENT") {
        return null;
    }

    const displayValue = toCustomerOrderProfitabilityDisplayAmount(lineKey, value);

    return (
        <Typography
            component="span"
            variant="body2"
            sx={(theme) => ({
                color: getComparisonAmountColor(displayValue, theme),
            })}
        >
            {formatCustomerOrderProfitabilityComparisonAmount(lineKey, displayValue, domesticCurrencyCode)}
        </Typography>
    );
};

const getComparisonAmountColor = (
    value: number,
    theme: {palette: {error: {main: string}, success: {main: string}}},
): string => {
    const isGood = isGoodCustomerOrderProfitabilityComparisonDisplayAmount(value);

    return isGood ? theme.palette.success.main : theme.palette.error.main;
};

const getCustomerOrderProfitabilityLineKeyLevel = (lineKey: CustomerOrderProfitabilityLineKey): number => {
    switch (lineKey) {
        case "REVENUE":
        case "CUSTOMER_ORDER_LINE_COSTS":
        case "PROJECT_MATERIAL_COSTS":
        case "OTHER_COSTS":
        case "TOTAL_COSTS":
        case "MARGIN":
        case "MARGIN_PERCENT":
            return 0;
        default:
            return 1;
    }
};
